import { defineComponent, h, markRaw, nextTick } from 'vue';
import utils from '@/Shared/utils.jsx';
import { tryReloadForStaleChunk } from '@/Shared/vitePreloadRecovery.js';
import debounce from 'lodash/debounce';
import EditorWorker from 'monaco-editor/esm/vs/editor/editor.worker.js?worker';
import JsonWorker from 'monaco-editor/esm/vs/language/json/json.worker.js?worker';
import TsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker.js?worker';

/** Vite `?worker` bundles; must be set before `import('monaco-editor')` so workers resolve as modules (base-path safe). */
function ensureMonacoEnvironmentWorkers() {
    const g = typeof globalThis !== 'undefined' ? globalThis : undefined;
    if (!g || typeof Worker === 'undefined')
        return;
    if (g.__monacoWorkersInstalled)
        return;
    g.MonacoEnvironment = {
        ...(g.MonacoEnvironment || {}),
        getWorker(_moduleId, label) {
            switch (label) {
                case 'json':
                    return new JsonWorker();
                case 'typescript':
                case 'javascript':
                    return new TsWorker();
                case 'editorWorkerService':
                default:
                    return new EditorWorker();
            }
        },
    };
    g.__monacoWorkersInstalled = true;
}

function hostLayoutSize(host) {
    if (!host)
        return { width: 400, height: 240 };
    const rect = host.getBoundingClientRect();
    let w = Math.floor(rect.width) || host.clientWidth || 0;
    let h = Math.floor(rect.height) || host.clientHeight || 0;
    // Use only the host's box — walking ancestors inflates height past the flex slot
    // (e.g. toolbar + pane titles inside the same column) and causes outer scrollbars.
    return {
        width: Math.max(200, w || 400),
        height: Math.max(120, h || 240),
    };
}

async function waitDomLayout() {
    await nextTick();
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
}

function coerceDocumentText(content) {
    if (content == null || content === '')
        return '';
    return typeof content === 'string' ? content : String(content);
}

function resolveMonacoLanguage(lang) {
    if (!lang || lang === 'text' || lang === 'text/plain')
        return 'plaintext';
    if (lang === 'csharp')
        return 'csharp';
    return lang;
}

function mapTheme(theme) {
    if (theme === 'monokai')
        return 'vs-dark';
    return 'vs';
}

function textStats(text) {
    const s = coerceDocumentText(text);
    const len = s.length;
    let lines = len === 0 ? 0 : 1;
    for (let i = 0; i < len; i++) {
        if (s.charCodeAt(i) === 10)
            lines++;
    }
    return { bytes: len, lines };
}

function createPerfMetrics() {
    return {
        domLayoutWaitMs: null,
        monacoImportMs: null,
        modelCreateMs: null,
        diffEditorCreateMs: null,
        setModelMs: null,
        editorMountMs: null,
        firstDiffComputeMs: null,
        lastDiffComputeMs: null,
        diffUpdateCount: 0,
        originalLines: null,
        originalBytes: null,
        modifiedLines: null,
        modifiedBytes: null,
        language: null,
        loadError: null,
    };
}

export default defineComponent({
    name: 'MonacoDiffEditor',
    emits: ['init', 'load-error', 'metrics-update'],
    props: {
        editorId: { type: String, default: '' },
        originalContent: { type: String, default: '' },
        modifiedContent: { type: String, default: '' },
        lang: { type: String, default: 'text/plain' },
        theme: { type: String, default: 'github' },
        readOnly: { type: Boolean, default: true },
        wordWrap: { type: Boolean, default: false },
        fontSize: { type: Number, default: 12 },
    },
    data() {
        return {
            uniqueid: null,
            diffEditor: null,
            monaco: null,
            originalModel: null,
            modifiedModel: null,
            isEditorMounted: false,
            isLoadingEditor: true,
            loadError: null,
            _activeLang: null,
            perfMetrics: createPerfMetrics(),
            _diffUpdateDisposable: null,
            _diffComputeStartedAt: null,
            _mountStartedAt: null,
        };
    },
    created() {
        this.uniqueid = utils.generateUUID();
    },
    computed: {
        realId() {
            if (this.editorId)
                return this.editorId + '_' + this.uniqueid;
            return 'MonacoDiffEditor_' + this.uniqueid;
        },
        monacoLanguage() {
            return resolveMonacoLanguage(this.lang);
        },
    },
    watch: {
        fontSize(value) {
            if (this.diffEditor)
                this.diffEditor.updateOptions({ fontSize: value });
            nextTick(() => this._layoutToHost?.());
        },
        wordWrap() {
            this.applyWordWrap();
        },
        originalContent(value) {
            this._updateModelContent('original', value);
        },
        modifiedContent(value) {
            this._updateModelContent('modified', value);
        },
        lang() {
            this._recreateModels();
        },
    },
    methods: {
        getPerfMetrics() {
            return { ...this.perfMetrics };
        },
        _emitPerfMetrics() {
            this.$emit('metrics-update', this.getPerfMetrics());
        },
        _disposeDiffUpdateListener() {
            if (this._diffUpdateDisposable) {
                this._diffUpdateDisposable.dispose();
                this._diffUpdateDisposable = null;
            }
        },
        _attachDiffUpdateListener() {
            this._disposeDiffUpdateListener();
            if (!this.diffEditor?.onDidUpdateDiff)
                return;
            this._diffComputeStartedAt = performance.now();
            this._diffUpdateDisposable = this.diffEditor.onDidUpdateDiff(() => {
                const elapsed = performance.now() - (this._diffComputeStartedAt ?? performance.now());
                this.perfMetrics.diffUpdateCount += 1;
                if (this.perfMetrics.diffUpdateCount === 1)
                    this.perfMetrics.firstDiffComputeMs = elapsed;
                this.perfMetrics.lastDiffComputeMs = elapsed;
                this._emitPerfMetrics();
            });
        },
        _recordModelStats(originalText, modifiedText, language) {
            const orig = textStats(originalText);
            const mod = textStats(modifiedText);
            this.perfMetrics.originalLines = orig.lines;
            this.perfMetrics.originalBytes = orig.bytes;
            this.perfMetrics.modifiedLines = mod.lines;
            this.perfMetrics.modifiedBytes = mod.bytes;
            this.perfMetrics.language = language;
        },
        getDiffEditor() {
            return this.diffEditor;
        },
        goToNextDiff() {
            this.diffEditor?.goToDiff?.('next');
        },
        goToPreviousDiff() {
            this.diffEditor?.goToDiff?.('previous');
        },
        revealFirstDiff() {
            this.diffEditor?.revealFirstDiff?.();
        },
        _scheduleRevealFirstDiff() {
            if (!this.diffEditor?.revealFirstDiff)
                return;
            nextTick(() => {
                requestAnimationFrame(() => {
                    this.diffEditor?.revealFirstDiff?.();
                });
            });
        },
        layout() {
            this._layoutToHost();
        },
        applyWordWrap() {
            if (!this.diffEditor)
                return;
            this.diffEditor.updateOptions({ wordWrap: this.wordWrap ? 'on' : 'off' });
            nextTick(() => this._layoutToHost?.());
        },
        _layoutToHost() {
            const host = this.$refs.editorHost;
            if (!this.diffEditor || !host)
                return;
            const { width, height } = hostLayoutSize(host);
            this.diffEditor.layout({ width, height });
        },
        _disposeModels() {
            if (this.originalModel) {
                this.originalModel.dispose();
                this.originalModel = null;
            }
            if (this.modifiedModel) {
                this.modifiedModel.dispose();
                this.modifiedModel = null;
            }
        },
        _createModels(monaco, language) {
            this._disposeModels();
            const originalText = coerceDocumentText(this.originalContent);
            const modifiedText = coerceDocumentText(this.modifiedContent);
            this.originalModel = markRaw(monaco.editor.createModel(originalText, language));
            this.modifiedModel = markRaw(monaco.editor.createModel(modifiedText, language));
            this._activeLang = language;
            if (this.diffEditor) {
                const t0 = performance.now();
                this.diffEditor.setModel({
                    original: this.originalModel,
                    modified: this.modifiedModel,
                });
                this.perfMetrics.setModelMs = performance.now() - t0;
                this._attachDiffUpdateListener();
                this._emitPerfMetrics();
                this._scheduleRevealFirstDiff();
            }
        },
        _recreateModels() {
            if (!this.monaco || !this.isEditorMounted)
                return;
            const language = this.monacoLanguage;
            if (language === this._activeLang && this.originalModel && this.modifiedModel) {
                this._updateModelContent('original', this.originalContent);
                this._updateModelContent('modified', this.modifiedContent);
                return;
            }
            this._createModels(this.monaco, language);
            nextTick(() => {
                this._layoutToHost();
                this._scheduleRevealFirstDiff();
            });
        },
        _updateModelContent(side, value) {
            const model = side === 'original' ? this.originalModel : this.modifiedModel;
            if (!model)
                return;
            const next = coerceDocumentText(value);
            if (model.getValue() === next)
                return;
            model.setValue(next);
            const language = this._activeLang || this.monacoLanguage;
            this._recordModelStats(
                side === 'original' ? next : this.originalModel?.getValue?.() ?? this.originalContent,
                side === 'modified' ? next : this.modifiedModel?.getValue?.() ?? this.modifiedContent,
                language,
            );
            if (this.diffEditor) {
                this._attachDiffUpdateListener();
                this._emitPerfMetrics();
            }
            this._scheduleRevealFirstDiff();
        },
    },
    render() {
        return (
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 'inherit', minWidth: 'inherit', position: 'relative' }}>
                {this.isLoadingEditor && (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255,255,255,0.7)',
                        zIndex: 2,
                        fontSize: '13px',
                    }}>Loading editor…</div>
                )}
                {this.loadError && (
                    <div style={{ padding: '8px', color: 'crimson', fontSize: '12px' }}>{this.loadError}</div>
                )}
                <div id={this.realId} ref="editorHost" style={{ flexGrow: 1, minHeight: 0, minWidth: 0, overflow: 'hidden', opacity: this.isLoadingEditor ? 0 : 1 }}></div>
            </div>
        );
    },
    async mounted() {
        this._mountStartedAt = performance.now();
        const tDom = performance.now();
        await waitDomLayout();
        this.perfMetrics.domLayoutWaitMs = performance.now() - tDom;
        const host = this.$refs.editorHost;
        if (!host || !host.isConnected) {
            utils.warn(`MonacoDiffEditor mount skipped; host not connected (${this.realId})`);
            this.isLoadingEditor = false;
            return;
        }
        try {
            ensureMonacoEnvironmentWorkers();
            const tImport = performance.now();
            const monaco = await import('monaco-editor');
            this.perfMetrics.monacoImportMs = performance.now() - tImport;
            this.monaco = markRaw(monaco);
            monaco.editor.setTheme(mapTheme(this.theme));

            const language = this.monacoLanguage;
            const originalText = coerceDocumentText(this.originalContent);
            const modifiedText = coerceDocumentText(this.modifiedContent);
            const tModels = performance.now();
            this.originalModel = markRaw(monaco.editor.createModel(originalText, language));
            this.modifiedModel = markRaw(monaco.editor.createModel(modifiedText, language));
            this.perfMetrics.modelCreateMs = performance.now() - tModels;
            this._activeLang = language;
            this._recordModelStats(originalText, modifiedText, language);

            const dimension = hostLayoutSize(host);
            await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

            const tDiffEditor = performance.now();
            this.diffEditor = markRaw(monaco.editor.createDiffEditor(host, {
                readOnly: this.readOnly,
                renderSideBySide: true,
                fontSize: this.fontSize,
                automaticLayout: false,
                dimension,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                wordWrap: this.wordWrap ? 'on' : 'off',
                tabSize: 4,
                fixedOverflowWidgets: true,
                bracketPairColorization: { enabled: false },
                unicodeHighlight: {
                    ambiguousCharacters: false,
                    invisibleCharacters: false,
                    nonBasicASCII: false,
                },
            }));
            this.perfMetrics.diffEditorCreateMs = performance.now() - tDiffEditor;

            const tSetModel = performance.now();
            this.diffEditor.setModel({
                original: this.originalModel,
                modified: this.modifiedModel,
            });
            this.perfMetrics.setModelMs = performance.now() - tSetModel;
            this._attachDiffUpdateListener();

            this._layoutDebounced = debounce(() => this._layoutToHost(), 120);
            this._layoutToHost();
            if (typeof ResizeObserver !== 'undefined') {
                this._hostResizeObserver = new ResizeObserver(() => this._layoutDebounced());
                this._hostResizeObserver.observe(host);
            }

            this.isEditorMounted = true;
            this.isLoadingEditor = false;
            this.applyWordWrap();
            this._scheduleRevealFirstDiff();
            this.perfMetrics.editorMountMs = performance.now() - (this._mountStartedAt ?? performance.now());
            this._emitPerfMetrics();
            this.$emit('init', this.diffEditor, monaco);
        }
        catch (err) {
            // Deploy skew: old tab → missing editor.main-*.js; reload once (see vitePreloadRecovery).
            if (tryReloadForStaleChunk(err))
                return;
            this.isLoadingEditor = false;
            this.loadError = err?.message || String(err);
            this.perfMetrics.loadError = this.loadError;
            this.perfMetrics.editorMountMs = performance.now() - (this._mountStartedAt ?? performance.now());
            this._emitPerfMetrics();
            utils.warn('MonacoDiffEditor failed to load', err);
            this.$emit('load-error', err);
        }
    },
    beforeUnmount() {
        this._disposeDiffUpdateListener();
        if (this._layoutDebounced?.cancel)
            this._layoutDebounced.cancel();
        this._layoutDebounced = null;
        if (this._hostResizeObserver) {
            this._hostResizeObserver.disconnect();
            this._hostResizeObserver = null;
        }
        if (this.diffEditor) {
            this.diffEditor.setModel(null);
            this.diffEditor.dispose();
            this.diffEditor = null;
        }
        this._disposeModels();
        this.monaco = null;
        this.isEditorMounted = false;
    },
});
